import {z} from 'zod';

export const WorkshopInterestStateSchema = z.enum(['NEW', 'OPEN', 'UNSUCCESSFUL']);
export type WorkshopInterestState = z.infer<typeof WorkshopInterestStateSchema>;

export const WorkshopInterestActionSchema = z.enum([
  'SET_SOURCE',
  'CHANGE_BRANCH',
  'CHANGE_CONTACT_PERSON',
  'DELETE',
  'OPEN',
  'END',
  'UPDATE_VEHICLE_DETAILS',
  'UPDATE_VEHICLE_INFORMATION',
  'SELECT_VEHICLE',
  'REMOVE_VEHICLE',
  'ADD_CUSTOMER_REQUEST',
  'UPDATE_CUSTOMER_REQUEST',
  'REMOVE_CUSTOMER_REQUEST',
  'UPDATE_VISIT_REQUEST',
  'UPDATE_ADDITIONAL_SERVICES',
  'CONTACT_CUSTOMER_VIA_EMAIL',
  'CONTACT_CUSTOMER_VIA_SMS',
  'CREATE_ACTIVITY',
]);
export type WorkshopInterestAction = z.infer<typeof WorkshopInterestActionSchema>;

export const WorkshopInterestResponseBodySchema = z.object({
  id: z.string(),
  publicId: z.string(),
  state: WorkshopInterestStateSchema,
  branchId: z.string(),
  sourceCodeId: z.string(),
  contactPersonId: z.string(),
  vehicleId: z.string().nullable(),
  createdBy: z.string(),
  createdAt: z.string(),
  updatedBy: z.string(),
  updatedAt: z.string(),
  firstContactAt: z.string().nullable(),
  availableActions: z.array(WorkshopInterestActionSchema),
});
export type WorkshopInterestResponseBody = z.infer<typeof WorkshopInterestResponseBodySchema>;

export const CreateWorkshopInterestRequestBodySchema = z.object({
  sourceCodeId: z.string(),
  contactPersonId: z.string(),
});
export type CreateWorkshopInterestRequestBody = z.infer<
  typeof CreateWorkshopInterestRequestBodySchema
>;

export type CreateWorkshopInterestApiArg = {
  createWorkshopInterestRequestBody: CreateWorkshopInterestRequestBody;
};

export const GetWorkshopInterestApiArgSchema = z.object({
  id: z.string(),
});
export type GetWorkshopInterestApiArg = z.infer<typeof GetWorkshopInterestApiArgSchema>;

export type GetWorkshopInterestApiResponse = WorkshopInterestResponseBody;

export const ChangeWorkshopInterestBranchRequestBodySchema = z.object({
  branchId: z.string(),
});
export type ChangeWorkshopInterestBranchRequestBody = z.infer<
  typeof ChangeWorkshopInterestBranchRequestBodySchema
>;

export type ChangeWorkshopInterestBranchApiArg = {
  id: string;
  changeWorkshopInterestBranchRequestBody: ChangeWorkshopInterestBranchRequestBody;
};

export const ChangeWorkshopInterestContactPersonRequestBodySchema = z.object({
  contactPersonId: z.string(),
});
export type ChangeWorkshopInterestContactPersonRequestBody = z.infer<
  typeof ChangeWorkshopInterestContactPersonRequestBodySchema
>;

export type ChangeWorkshopInterestContactPersonApiArg = {
  id: string;
  changeWorkshopInterestContactPersonRequestBody: ChangeWorkshopInterestContactPersonRequestBody;
};

export const SetWorkshopInterestSourceRequestBodySchema = z.object({
  sourceCodeId: z.string(),
});
export type SetWorkshopInterestSourceRequestBody = z.infer<
  typeof SetWorkshopInterestSourceRequestBodySchema
>;

export type SetWorkshopInterestSourceApiArg = {
  id: string;
  setWorkshopInterestSourceRequestBody: SetWorkshopInterestSourceRequestBody;
};

export const WorkshopInterestActivityResponseBodySchema = z.object({
  id: z.string(),
  interestId: z.string().nullable().optional(),
  businessCaseId: z.string().nullable().optional(),
  businessCaseCode: z.string().nullable().optional(),
  businessCaseType: z.string().nullable().optional(),
  subject: z.string().nullable().optional(),
  activityType: z.string(),
  userId: z.string().nullable(),
  userName: z.string(),
  timestamp: z.string(),
  description: z.string().nullable(),
  relatedTask: z.unknown().nullable().optional(),
  relatedMessage: z.unknown().nullable().optional(),
  relatedCall: z.unknown().nullable().optional(),
  reasonCloseCodeId: z.string().nullable().optional(),
  reasonCloseNote: z.string().nullable().optional(),
  isEditable: z.boolean().optional(),
});
export type WorkshopInterestActivityResponseBody = z.infer<
  typeof WorkshopInterestActivityResponseBodySchema
>;

export const ListWorkshopInterestActivitiesApiArgSchema = z.object({
  interestId: z.string(),
});
export type ListWorkshopInterestActivitiesApiArg = z.infer<
  typeof ListWorkshopInterestActivitiesApiArgSchema
>;

export type ListWorkshopInterestActivitiesApiResponse = WorkshopInterestActivityResponseBody[];

export type DeleteWorkshopInterestApiArg = {
  id: string;
};

export type OpenWorkshopInterestApiArg = {
  id: string;
};

export const EndWorkshopInterestRequestBodySchema = z.object({
  closeReasonCodeId: z.string(),
  closeNote: z.string().nullable(),
});
export type EndWorkshopInterestRequestBody = z.infer<typeof EndWorkshopInterestRequestBodySchema>;

export type EndWorkshopInterestApiArg = {
  id: string;
  endWorkshopInterestRequestBody: EndWorkshopInterestRequestBody;
};

export const WorkshopActivityManualTypeSchema = z.enum([
  'CALL_FROM_CUSTOMER',
  'CALL_TO_CUSTOMER',
  'MESSAGE_FROM_CUSTOMER',
  'MESSAGE_TO_CUSTOMER',
  'CUSTOMER_VISIT',
]);
export type WorkshopActivityManualType = z.infer<typeof WorkshopActivityManualTypeSchema>;

export const CreateWorkshopInterestActivityRequestBodySchema = z.object({
  activityType: WorkshopActivityManualTypeSchema,
  createdAt: z.string(),
  description: z.string().nullable(),
  subject: z.string().nullable(),
});
export type CreateWorkshopInterestActivityRequestBody = z.infer<
  typeof CreateWorkshopInterestActivityRequestBodySchema
>;

export type CreateWorkshopInterestActivityApiArg = {
  interestId: string;
  createWorkshopInterestActivityRequestBody: CreateWorkshopInterestActivityRequestBody;
};

export type CreateWorkshopInterestActivityApiResponse = {
  id: string;
};

export const SelectVehicleRequestBodySchema = z.object({
  vehicleId: z.string().uuid(),
});
export type SelectVehicleRequestBody = z.infer<typeof SelectVehicleRequestBodySchema>;

export type SelectVehicleApiArg = {
  id: string;
  selectVehicleRequestBody: SelectVehicleRequestBody;
};

export type RemoveVehicleApiArg = {
  id: string;
};
